import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Avatar,
  Dialog,
  IconButton,
  Button,
  Stack,
  TextField,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MicIcon from "@mui/icons-material/Mic";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import SchoolIcon from "@mui/icons-material/School";
import ArticleIcon from "@mui/icons-material/Article";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { useNavigate } from "react-router-dom";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import axios from "axios";
import { useTTSQueue } from "hooks/useTTSQueue";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useGetUserDetailsMutation } from "../redux/api/authApi";
import { setCookie, getCookie, removeCookie } from '../utils/storageUtils';
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ImageIcon from "@mui/icons-material/Image";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VerifiedIcon from "@mui/icons-material/Verified";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BalanceIcon from "@mui/icons-material/Balance";
import PublicIcon from "@mui/icons-material/Public";
import { getVoiceClipUrl } from "../utils/voiceClips";
import { getCurrentLang } from "../utils/googleTranslate";

const TWIN_GREETING = "Hi {0}, I am your Digital Twin. Tell me what content you'd like me to create or refine on your dashboard.";

interface Props {
  userName?: string;
  userImage?: string;
  handleRefresh?: (
    prompt?: string,
    options?: {
      content_types?: { type: string; quantity: number }[];
      mode?: "recommend" | "recommend_generate";
      knowledge_mode?: "faithful" | "balanced" | "expanded";
    }
  ) => void;
}

/* ─── Digital Twin welcome-sound mute preference (persisted for the session) ───
   The popup component unmounts/remounts on page navigation (e.g. leaving and
   returning to the Authority Engine page), so any mute flag that lives only
   in local component/hook state resets to its default. We back it with
   sessionStorage so "muted" survives a remount and only changes when the
   user explicitly toggles it again. Swap sessionStorage -> localStorage
   below if the preference should also survive closing the browser tab. */
const DIGITAL_TWIN_MUTE_KEY = "digitalTwinWelcomeMuted";

const getStoredDigitalTwinMute = (): boolean => {
  try {
    return sessionStorage.getItem(DIGITAL_TWIN_MUTE_KEY) === "true";
  } catch {
    return false;
  }
};

const setStoredDigitalTwinMute = (muted: boolean): void => {
  try {
    sessionStorage.setItem(DIGITAL_TWIN_MUTE_KEY, String(muted));
  } catch {
    // sessionStorage unavailable (e.g. private mode) — fail silently,
    // mute just won't persist across remounts in that case.
  }
};

/* ─── Content type config for "Tell me what to create" ─── */
const CONTENT_TYPE_CONFIG = [
  { key: "social_posts", label: "Social Posts", desc: "LinkedIn, X, Facebook & more", Icon: ChatBubbleOutlineIcon, color: "#8B5CF6" },
  { key: "blogs", label: "Blogs", desc: "Articles, guides & insights", Icon: ArticleIcon, color: "#10B981" },
  { key: "videos", label: "Videos", desc: "Video clips, YouTube, Reels", Icon: OndemandVideoIcon, color: "#EF4444" },
  { key: "courses", label: "Courses", desc: "Lessons, modules & curricula", Icon: SchoolIcon, color: "#3B82F6" },
  { key: "images", label: "Images", desc: "Graphics, banners & visuals", Icon: ImageIcon, color: "#F59E0B" },
] as const;

type ContentTypeKey = typeof CONTENT_TYPE_CONFIG[number]["key"];

type KnowledgeMode = "faithful" | "balanced" | "expanded";

const KNOWLEDGE_MODE_CONFIG: {
  key: KnowledgeMode;
  title: string;
  desc: string;
  badge: string;
  badgeColor: string;
  Icon: typeof DescriptionOutlinedIcon;
  iconColor: string;
}[] = [
  {
    key: "faithful",
    title: "Faithful to my content",
    desc: "Use only your uploaded materials. I'll stay close to your original content.",
    badge: "Best for accuracy",
    badgeColor: "#A78BFA",
    Icon: DescriptionOutlinedIcon,
    iconColor: "#A78BFA",
  },
  {
    key: "balanced",
    title: "Balanced (Recommended)",
    desc: "Use your content as the foundation and enhance with AI insights, examples and depth.",
    badge: "Best overall results",
    badgeColor: "#34D399",
    Icon: BalanceIcon,
    iconColor: "#34D399",
  },
  {
    key: "expanded",
    title: "Expanded with AI",
    desc: "Use my broader knowledge to create complete content even if it's not in your uploaded materials.",
    badge: "Best for new topics",
    badgeColor: "#60A5FA",
    Icon: PublicIcon,
    iconColor: "#60A5FA",
  },
];

/* ─── Brain SVG Illustration ─── */
const BrainIllustration = () => (
  <svg width="92" height="84" viewBox="0 0 120 110" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="brainGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#A78BFA" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="brainGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#C4B5FD" />
        <stop offset="60%" stopColor="#8B5CF6" />
        <stop offset="100%" stopColor="#4F46E5" />
      </linearGradient>
      <filter id="glow">
        <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
        <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>
    {/* Glow halo */}
    <ellipse cx="60" cy="55" rx="52" ry="48" fill="url(#brainGlow)" />
    {/* Outer ring */}
    <ellipse cx="60" cy="55" rx="46" ry="42" stroke="#7C3AED" strokeWidth="0.5" strokeDasharray="3 4" fill="none" opacity="0.5" />
    {/* Brain shape - left hemisphere */}
    <path d="M60 28 C48 26 36 30 30 40 C24 50 26 62 30 68 C34 74 40 78 46 78 L60 78 Z" fill="url(#brainGrad)" filter="url(#glow)" opacity="0.9" />
    {/* Brain shape - right hemisphere */}
    <path d="M60 28 C72 26 84 30 90 40 C96 50 94 62 90 68 C86 74 80 78 74 78 L60 78 Z" fill="url(#brainGrad)" filter="url(#glow)" opacity="0.85" />
    {/* Center divide */}
    <line x1="60" y1="28" x2="60" y2="78" stroke="#312E81" strokeWidth="1" opacity="0.6" />
    {/* Neural lines */}
    <path d="M42 42 Q50 50 58 46" stroke="#C4B5FD" strokeWidth="0.8" fill="none" opacity="0.8" />
    <path d="M38 54 Q46 50 54 56" stroke="#A78BFA" strokeWidth="0.8" fill="none" opacity="0.7" />
    <path d="M44 64 Q52 60 58 66" stroke="#C4B5FD" strokeWidth="0.8" fill="none" opacity="0.6" />
    <path d="M78 42 Q70 50 62 46" stroke="#C4B5FD" strokeWidth="0.8" fill="none" opacity="0.8" />
    <path d="M82 54 Q74 50 66 56" stroke="#A78BFA" strokeWidth="0.8" fill="none" opacity="0.7" />
    <path d="M76 64 Q68 60 62 66" stroke="#C4B5FD" strokeWidth="0.8" fill="none" opacity="0.6" />
    {/* Nodes */}
    {[[42, 42], [38, 54], [44, 64], [58, 46], [54, 56], [58, 66], [78, 42], [82, 54], [76, 64], [62, 46], [66, 56], [62, 66]].map(([cx, cy], i) => (
      <circle key={i} cx={cx} cy={cy} r="2.5" fill="#E0D7FF" opacity="0.9" filter="url(#glow)" />
    ))}
    {/* Stars / sparkles */}
    <g filter="url(#glow)" opacity="0.9">
      <path d="M100 18 L101.5 22 L106 23.5 L101.5 25 L100 29 L98.5 25 L94 23.5 L98.5 22 Z" fill="#F5F3FF" />
      <path d="M16 30 L17 33 L20 34 L17 35 L16 38 L15 35 L12 34 L15 33 Z" fill="#DDD6FE" />
      <path d="M108 70 L109 72 L111 73 L109 74 L108 76 L107 74 L105 73 L107 72 Z" fill="#C4B5FD" />
      <circle cx="20" cy="68" r="2" fill="#A78BFA" />
      <circle cx="105" cy="42" r="1.5" fill="#E0D7FF" />
    </g>
  </svg>
);

/* ─── Trigger Card ─── */
const TriggerCard: React.FC<{ userImage: string; onClick: () => void; onClear?: () => void; hasCustomization?: boolean; }> = ({ userImage, onClick, onClear, hasCustomization }) => (
  <Box
    onClick={onClick}
    sx={{
      width: "100%",
      cursor: "pointer",
      borderRadius: "22px",
      overflow: "hidden",
      background: "linear-gradient(160deg, #0F1629 0%, #0A0D1A 100%)",
      border: "1px solid rgba(124,58,237,0.25)",
      p: 2.5,
      position: "relative",
      transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
      boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
      "&:hover": {
        transform: "translateY(-3px) scale(1.01)",
        borderColor: "#7C3AED",
        boxShadow: "0 16px 40px rgba(124,58,237,0.25), inset 0 1px 0 rgba(255,255,255,0.08)",
      },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: 0, right: 0,
        height: "1px",
        background: "linear-gradient(90deg, transparent, rgba(167,139,250,0.5), transparent)",
      },
    }}
  >
    {/* Avatar with ring */}
    <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5, position: "relative" }}>
      <Box sx={{
        p: "3px",
        borderRadius: "50%",
        background: "linear-gradient(135deg, #7C3AED, #3B82F6, #7C3AED)",
        backgroundSize: "200% 200%",
        animation: "spinGrad 4s linear infinite",
        "@keyframes spinGrad": { "0%": { backgroundPosition: "0% 50%" }, "100%": { backgroundPosition: "200% 50%" } },
      }}>
        <Avatar src={userImage} sx={{
          width: 70, height: 70, border: "2px solid #0A0D1A", "& img": {
            objectFit: "cover",
            objectPosition: "top",
          },
        }} />
      </Box>
      {/* Online dot */}
      <Box sx={{
        position: "absolute", bottom: 4, right: "calc(50% - 38px)",
        width: 12, height: 12, borderRadius: "50%",
        background: "#10B981",
        border: "2px solid #0A0D1A",
        boxShadow: "0 0 8px #10B981",
        animation: "pulse 2s infinite",
        "@keyframes pulse": { "0%,100%": { boxShadow: "0 0 8px #10B981" }, "50%": { boxShadow: "0 0 16px #10B981" } },
      }} />
    </Box>

    <Stack alignItems="center" spacing={0.5} mb={2}>
      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15, letterSpacing: "-0.2px" }}>
        My Digital Twin <AutoAwesomeIcon sx={{ fontSize: 14, color: "#A78BFA", verticalAlign: "middle" }} />
      </Typography>
      <Typography sx={{ color: "#10B981", fontSize: 11, letterSpacing: "0.3px" }}>
        ● Creating for you 24/7
      </Typography>
    </Stack>

    {/* Subtitle box */}
    <Box sx={{
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: "12px",
      p: 1.2,
      textAlign: "center",
    }}>
      <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 11, textAlign: "center" }}>
        Click me to customize the content<br />I create for you.
      </Typography>

      <Button
        fullWidth
        startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
        sx={{
          textTransform: "none",
          background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
          color: "#fff",
          borderRadius: "12px",
          fontSize: 12,
          fontWeight: 600,
          py: 0.9,
          mt: 1,
          boxShadow: "0 4px 15px rgba(124,58,237,0.4)",
          "&:hover": { background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)", boxShadow: "0 6px 20px rgba(124,58,237,0.5)" },
        }}
      >
        Customize My Content
      </Button>
      {hasCustomization && (
        <Button
          startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
          fullWidth
          size="small"
          color="error"
          variant="text"
          onClick={(e) => {
            e.stopPropagation(); // prevent opening popup
            onClear?.();
          }}
          sx={{
            mt: 1,
            textTransform: "none",
            fontSize: 11,
            color: "#FF6B6B",
            "&:hover": {
              background: "rgba(255,107,107,0.08)",
            },
          }}
        >
          Clear Customization
        </Button>
      )}
    </Box>
  </Box>
);

/* ─── Main Component ─── */
const MyDigitalTwinPopup: React.FC<Props> = ({
  userName = "",
  userImage = "",
  handleRefresh,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const STORAGE_KEY = "digital_twin_prompt";
  const CONTENT_TYPES_STORAGE_KEY = "digital_twin_content_types";
  const EXPIRY_TIME = 24 * 60 * 60 * 1000; // 1 day
  const user_email = localStorage.getItem('user_email') || getCookie('user_email') || '';
  const [getUserDetailsMutation] = useGetUserDetailsMutation();
  const [userDetails, setUserDetails] = useState<any>();
  const [customContent, setCustomContent] = useState<string>("");
  const [isSending, setIsSending] = useState(false);
  const DEFAULT_CONTENT_TYPE_COUNTS: Record<ContentTypeKey, number> = {
    social_posts: 2,
    blogs: 2,
    videos: 2,
    courses: 2,
    images: 2,
  };
  const MAX_CONTENT_TYPE_COUNT = 7;

  const loadStoredContentTypeState = (): {
    counts: Record<ContentTypeKey, number>;
    mode: "recommend" | "recommend_generate";
    knowledgeMode: KnowledgeMode;
  } => {
    try {
      const raw = localStorage.getItem(CONTENT_TYPES_STORAGE_KEY);
      if (!raw) return { counts: DEFAULT_CONTENT_TYPE_COUNTS, mode: "recommend", knowledgeMode: "balanced" };

      const parsed = JSON.parse(raw);
      const isExpired = Date.now() - parsed.timestamp > EXPIRY_TIME;
      if (isExpired) {
        localStorage.removeItem(CONTENT_TYPES_STORAGE_KEY);
        return { counts: DEFAULT_CONTENT_TYPE_COUNTS, mode: "recommend", knowledgeMode: "balanced" };
      }

      const mergedCounts = { ...DEFAULT_CONTENT_TYPE_COUNTS, ...(parsed.counts || {}) };
      const clampedCounts = Object.fromEntries(
        Object.entries(mergedCounts).map(([key, value]) => [
          key,
          Math.min(MAX_CONTENT_TYPE_COUNT, Math.max(0, Number(value) || 0)),
        ])
      ) as Record<ContentTypeKey, number>;

      const validKnowledgeModes: KnowledgeMode[] = ["faithful", "balanced", "expanded"];
      const knowledgeMode: KnowledgeMode = validKnowledgeModes.includes(parsed.knowledge_mode)
        ? parsed.knowledge_mode
        : "balanced";

      return {
        counts: clampedCounts,
        mode: parsed.mode === "recommend_generate" ? "recommend_generate" : "recommend",
        knowledgeMode,
      };
    } catch {
      return { counts: DEFAULT_CONTENT_TYPE_COUNTS, mode: "recommend", knowledgeMode: "balanced" };
    }
  };

  const [committedCounts, setCommittedCounts] = useState<Record<ContentTypeKey, number>>(
    () => loadStoredContentTypeState().counts
  );
  const [committedMode, setCommittedMode] = useState<"recommend" | "recommend_generate">(
    () => loadStoredContentTypeState().mode
  );

  const [committedKnowledgeMode, setCommittedKnowledgeMode] = useState<KnowledgeMode>(
    () => loadStoredContentTypeState().knowledgeMode
  );
  const [contentTypeCounts, setContentTypeCounts] = useState<Record<ContentTypeKey, number>>(committedCounts);
  const [assistMode, setAssistMode] = useState<"recommend" | "recommend_generate">(committedMode);
  const [knowledgeMode, setKnowledgeMode] = useState<KnowledgeMode>(committedKnowledgeMode);

  useEffect(() => {
    if (open) {
      setContentTypeCounts(committedCounts);
      setAssistMode(committedMode);
      setKnowledgeMode(committedKnowledgeMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [lastBotText, setLastBotText] = useState<string>("");
  const prevTextRef = useRef("");
  const [isRecordingQuestion, setIsRecordingQuestion] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  const { playText, stopAll, isPlaying, audioRef, isMuted, setIsMuted } =
    useTTSQueue();

  // useTTSQueue() re-initializes isMuted to its own default every time this
  // component mounts (e.g. after navigating away and back), so pull the
  // last-known preference back in as soon as we mount.
  useEffect(() => {
    setIsMuted(getStoredDigitalTwinMute());
  }, []);

  const handleClose = () => {
    stopAll();
    setOpen(false);
  };

  const fetchData = async () => {
    try {
      const response = await getUserDetailsMutation({ email: user_email || getCookie('user_email') || '', filter: '', start_date: '', end_date: '' });

      if ('data' in response) {
        setUserDetails(response.data.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const storedData = localStorage.getItem(STORAGE_KEY);

    if (!storedData) return;

    try {
      const parsed = JSON.parse(storedData);

      const isExpired =
        Date.now() - parsed.timestamp > EXPIRY_TIME;

      if (isExpired) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      setCustomContent(parsed.text || "");
    } catch (err) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const savePromptToLocalStorage = (text: string) => {
    if (!text.trim()) return;

    const data = {
      text,
      timestamp: Date.now(),
    };

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(data)
    );
  };

  const COMING_SOON_TYPES: ContentTypeKey[] = ["images"];
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [comingSoonLabel, setComingSoonLabel] = useState("");

  const handleComingSoon = (label: string) => {
    setComingSoonLabel(label);
    setComingSoonOpen(true);
  };

  const updateContentTypeCount = (key: ContentTypeKey, delta: number) => {
    if (COMING_SOON_TYPES.includes(key)) {
      const ct = CONTENT_TYPE_CONFIG.find((c) => c.key === key);
      handleComingSoon(ct?.label || "This");
      return;
    }
    setContentTypeCounts((prev) => ({
      ...prev,
      [key]: Math.min(MAX_CONTENT_TYPE_COUNT, Math.max(0, prev[key] + delta)),
    }));
  };

  const hasUnsavedCountChanges =
    assistMode !== committedMode ||
    knowledgeMode !== committedKnowledgeMode ||
    CONTENT_TYPE_CONFIG.some((ct) => contentTypeCounts[ct.key] !== committedCounts[ct.key]);

  const handleSend = async () => {
    const content_types = CONTENT_TYPE_CONFIG.map((ct) => ({
      type: ct.key,
      quantity: contentTypeCounts[ct.key],
    }));

    try {
      setIsSending(true);

      savePromptToLocalStorage(customContent.trim());

      setCommittedCounts(contentTypeCounts);
      setCommittedMode(assistMode);
      setCommittedKnowledgeMode(knowledgeMode);
      localStorage.setItem(
        CONTENT_TYPES_STORAGE_KEY,
        JSON.stringify({
          counts: contentTypeCounts,
          mode: assistMode,
          knowledge_mode: knowledgeMode,
          timestamp: Date.now(),
        })
      );

      toast.success("Customization saved!");
      toast.success("Refreshing personalized content for you...");

      handleRefresh?.(customContent.trim(), { content_types, mode: assistMode, knowledge_mode: knowledgeMode });

      handleClose();

    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleClearCustomization = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CONTENT_TYPES_STORAGE_KEY);

    setCustomContent("");
    setContentTypeCounts(DEFAULT_CONTENT_TYPE_COUNTS);
    setAssistMode("recommend");
    setKnowledgeMode("balanced");
    setCommittedCounts(DEFAULT_CONTENT_TYPE_COUNTS);
    setCommittedMode("recommend");

    toast.success("Customization cleared");

    handleRefresh?.();
  };

  useEffect(() => {
    if (!lastBotText) return;
    if (prevTextRef.current === lastBotText) return;
    prevTextRef.current = lastBotText;
    stopAll();
    playText(lastBotText);
  }, [lastBotText]);

  const startRecording = async (target: "question") => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
    audioChunksRef.current = [];
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunksRef.current.push(e.data);
    };
    mediaRecorder.onstop = () => sendAudioToBackend(target);
    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setIsRecordingQuestion(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecordingQuestion(false);
  };

  const sendAudioToBackend = async (target: "question") => {
    const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
    const formData = new FormData();
    formData.append("audio", audioBlob, "speech.webm");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SPEECH_TO_TEXT_SERVICE}/speech_to_text`,
        { method: "POST", body: formData }
      );
      const data = await res.json();
      setCustomContent((prev) => `${prev} ${data.text}`.trim());
    } catch (err) {
      console.error("Speech upload failed", err);
    }
  };

  const toggleMute = () => {
    setIsMuted((prev) => {
      const newState = !prev;
      setStoredDigitalTwinMute(newState);
      if (audioRef.current) {
        if (newState) audioRef.current.pause();
        else audioRef.current.play().catch(() => { });
      }
      return newState;
    });
  };

  const secondAudio = "https://tts.ocesha.ai/media/a5264db5-662f-4fcd-842c-6bd97c77b9b4.wav";

  useEffect(() => {
    if (open && userName) {
      fetchAudio();
    }
  }, [open, userName]);

  const fetchAudio = async () => {
    // Read the persisted flag directly instead of the isMuted state value:
    // this effect can run in the same commit as the mount-sync effect above,
    // before that effect's setIsMuted has actually re-rendered the
    // component, so the `isMuted` closure here can still be stale. Storage
    // is always current. Bail out early so a muted user doesn't even pay
    // for the TTS request.
    if (getStoredDigitalTwinMute()) return;

    try {
      let voiceName = userDetails?.digitial_twin_voice
        ? userDetails.digitial_twin_voice.replace(/\.wav$/i, "")
        : "default";

      voiceName = `${voiceName}.wav`;

      // Site language other than English: translated greeting, WAV generated once per
      // language/voice on the server and reused (see utils/voiceClips.ts)
      const localized = getCurrentLang() === "en" ? null : await getVoiceClipUrl(TWIN_GREETING, { voice: voiceName, vars: [userName || ""] })
        .catch(() => null);
      if (getCurrentLang() !== "en" && localized) { playAudio(localized); return; }

      const res = await axios.post(`${process.env.REACT_APP_TEXT_TO_SPEECH_SERVICE}/speak?voice=${voiceName}&text=${encodeURIComponent(`Hi ${userName}, I am your Digital Twin. Tell me what content you'd like me to create or refine on your dashboard.`)}`);
      if (res?.data?.audio_path) playAudio(res.data.audio_path);
    } catch (err) { console.log(err); }
  };

  const playAudio = (url: string) => {
    const audio = new Audio(url);
    audioRef.current = audio; audio.muted = getStoredDigitalTwinMute();
    audio.play().catch(() => {
      const retry = () => { audio.play().catch(() => { }); document.removeEventListener("click", retry); document.removeEventListener("keydown", retry); };
      document.addEventListener("click", retry, { once: true });
      document.addEventListener("keydown", retry, { once: true });
    });
    audio.onended = () => { };
  };

  const iconBtnBase = {
    width: 38,
    height: 38,
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.1)",
    transition: "all 0.2s ease",
    "&:hover": { transform: "scale(1.06)" },
  };

  return (
    <>
      <TriggerCard
        userImage={userImage}
        hasCustomization={!!customContent}
        onClear={handleClearCustomization}
        onClick={() => {
          stopAll();

          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }

          setOpen(true);
        }} />

      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason === "backdropClick") return;
          handleClose();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: "linear-gradient(160deg, #0E1525 0%, #070B14 100%)",
            borderRadius: "28px",
            border: "1px solid rgba(238, 235, 243, 0.99)",
            overflow: "hidden",
            boxShadow: "0 40px 80px rgba(247, 238, 238, 0.0), 0 0 0 0px rgb(255, 255, 255)",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: "1px",
              background: "linear-gradient(90deg, transparent, rgba(167,139,250,0.6), transparent)",
            },
          },
        }}
        BackdropProps={{ sx: { backdropFilter: "blur(8px)", background: "rgba(0,0,0,0.7)" } }}
      >
        <Box
          sx={{
            p: 2,
            position: "relative",
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
            scrollbarWidth: "auto",
            scrollbarColor: "rgba(167,139,250,0.6) rgba(255,255,255,0.04)",
            "&::-webkit-scrollbar": { width: "10px" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(167,139,250,0.55)",
              borderRadius: "6px",
              border: "2px solid transparent",
              backgroundClip: "padding-box",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "rgba(167,139,250,0.8)",
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(255,255,255,0.03)",
              borderRadius: "6px",
            },
          }}
        >

          {/* ── Header ── */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ position: "relative" }}>
                <Avatar src={userImage} sx={{ width: 44, height: 44, border: "2px solid #7C3AED" }} />
                <Box sx={{
                  position: "absolute", bottom: 0, right: 0,
                  width: 10, height: 10, borderRadius: "50%",
                  background: "#10B981", border: "2px solid #070B14",
                }} />
              </Box>
              <Box>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
                  My Digital Twin <AutoAwesomeIcon sx={{ fontSize: 13, color: "#A78BFA", verticalAlign: "middle" }} />
                </Typography>
                <Typography sx={{ color: "#10B981", fontSize: 11 }}>
                  ● Online · Creating for you 24/7
                </Typography>
              </Box>
            </Stack>
            <IconButton
              onClick={handleClose}
              sx={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                "&:hover": { background: "rgba(255,255,255,0.1)" },
              }}
            >
              <CloseIcon sx={{ color: "#fff", fontSize: 18 }} />
            </IconButton>
          </Stack>

          {/* ── Hero Banner ── */}
          <Box sx={{
            pt: 0,
            pb: 1.6,
            px: 2,
            borderRadius: "20px",
            background: "linear-gradient(135deg, rgba(124,58,237,0.15) 0%, rgba(59,130,246,0.08) 100%)",
            border: "1px solid rgba(124,58,237,0.2)",
            mb: 1.5,
            position: "relative",
            overflow: "hidden",
          }}>
            <Box sx={{
              position: "absolute", inset: 0,
              background: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E\")",
              opacity: 0.4, pointerEvents: "none",
            }} />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              {/* Left */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 20,
                    mb: 0.75,
                  }}
                >
                  Hi {userName}!<br />
                  I'm your Digital Twin.
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: 13,
                  }}
                >
                  Tell me what content you'd like me to create
                  <br />
                  or refine on your dashboard.
                </Typography>
              </Box>

              {/* Right */}
              <Stack
                spacing={1}
                alignItems="center"
              >
                <BrainIllustration />

                <Button
                  startIcon={<CloudUploadIcon />}
                  onClick={() => {
                    stopAll();
                    localStorage.setItem("activeItem", "digital");
                    navigate(0);
                  }}
                  sx={{
                    textTransform: "none",
                    background:
                      "linear-gradient(135deg,#2563EB,#1D4ED8)",
                    color: "#fff",
                    borderRadius: "10px",
                    px: 2.5,
                    fontWeight: 600,
                  }}
                >
                  Train My Digital Twin
                </Button>
              </Stack>
            </Stack>
          </Box>

          {/* ── Tell me what to create ── */}
          <Box sx={{ mb: 1.25 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.3 }}>
              <AutoAwesomeIcon sx={{ fontSize: 16, color: "#A78BFA" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>
                1. Tell me what to create
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)", mb: 1 }}>
              Choose the content types and how many you'd like me to create.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))",
                gap: 0.75,
              }}
            >
              {CONTENT_TYPE_CONFIG.map((ct) => {
                const { Icon } = ct;
                const count = contentTypeCounts[ct.key];
                const active = count > 0;
                const isComingSoon = COMING_SOON_TYPES.includes(ct.key);
                return (
                  <Box
                    key={ct.key}
                    onClick={isComingSoon ? () => handleComingSoon(ct.label) : undefined}
                    sx={{
                      position: "relative",
                      bgcolor: active && !isComingSoon ? "rgba(124,58,237,0.1)" : "rgba(255,255,255,0.02)",
                      border: "1px solid",
                      borderColor: active && !isComingSoon ? "rgba(124,58,237,0.5)" : "rgba(255,255,255,0.08)",
                      borderRadius: "14px",
                      p: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 0.4,
                      transition: "all 0.2s ease",
                      cursor: isComingSoon ? "pointer" : "default",
                      opacity: isComingSoon ? 0.55 : 1,
                      overflow: "hidden",
                      "&:hover": isComingSoon ? { opacity: 0.75 } : undefined,
                    }}
                  >
                    <Icon sx={{ fontSize: 18, color: ct.color }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
                      {ct.label}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.45)", lineHeight: 1.3, minHeight: 20 }}>
                      {ct.desc}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: "100%", mt: 0.4 }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateContentTypeCount(ct.key, -1);
                        }}
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "6px",
                          bgcolor: "rgba(255,255,255,0.06)",
                          color: "#9ca3af",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                        }}
                      >
                        <RemoveIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
                        {count}
                      </Typography>
                      <IconButton
                        size="small"
                        disabled={count >= MAX_CONTENT_TYPE_COUNT}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateContentTypeCount(ct.key, 1);
                        }}
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "6px",
                          bgcolor: "rgba(124,58,237,0.18)",
                          color: "#a78bfa",
                          "&:hover": { bgcolor: "rgba(124,58,237,0.3)" },
                          "&.Mui-disabled": {
                            bgcolor: "rgba(255,255,255,0.04)",
                            color: "rgba(167,139,250,0.35)",
                          },
                        }}
                      >
                        <AddIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Stack>
                  </Box>
                );
              })}
            </Box>

            {hasUnsavedCountChanges && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.6}
                sx={{
                  mt: 1,
                  px: 1.2,
                  py: 0.6,
                  borderRadius: "8px",
                  bgcolor: "rgba(167,139,250,0.1)",
                  border: "1px solid rgba(167,139,250,0.25)",
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 14, color: "#A78BFA" }} />
                <Typography sx={{ fontSize: 11, color: "#C4B5FD" }}>
                  Click <b>Submit</b> to apply your changes — they won't be saved otherwise.
                </Typography>
              </Stack>
            )}
          </Box>

          {/* ── 2. How should I use your knowledge? ── */}
          <Box sx={{ mb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.3 }}>
              <AutoAwesomeIcon sx={{ fontSize: 16, color: "#A78BFA" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>
                2. How should I use your knowledge?
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)", mb: 1 }}>
              Choose how closely I should use your uploaded materials vs. my broader knowledge.{" "}
              <InfoOutlinedIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.35)", verticalAlign: "middle" }} />
            </Typography>

            <Stack direction="row" spacing={1.2}>
              {KNOWLEDGE_MODE_CONFIG.map((opt) => {
                const selected = knowledgeMode === opt.key;
                const { Icon } = opt;
                return (
                  <Box
                    key={opt.key}
                    onClick={() => setKnowledgeMode(opt.key)}
                    sx={{
                      flex: 1,
                      cursor: "pointer",
                      borderRadius: "14px",
                      border: "1px solid",
                      borderColor: selected ? "rgba(124,58,237,0.6)" : "rgba(255,255,255,0.08)",
                      bgcolor: selected ? "rgba(124,58,237,0.1)" : "rgba(255,255,255,0.02)",
                      p: 1.1,
                      transition: "all 0.2s ease",
                    }}
                  >
                    <Stack direction="row" spacing={0.8} alignItems="flex-start" sx={{ mb: 0.6 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          border: `2px solid ${selected ? "#7C3AED" : "rgba(255,255,255,0.25)"}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: "1px",
                        }}
                      >
                        {selected && (
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: "#7C3AED" }} />
                        )}
                      </Box>
                      <Icon sx={{ fontSize: 17, color: opt.iconColor, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
                        {opt.title}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.5)", lineHeight: 1.5, mb: 0.8 }}>
                      {opt.desc}
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-block",
                        fontSize: 10,
                        fontWeight: 700,
                        color: opt.badgeColor,
                        background: `${opt.badgeColor}1A`,
                        border: `1px solid ${opt.badgeColor}4D`,
                        borderRadius: "999px",
                        px: 1,
                        py: 0.3,
                      }}
                    >
                      {opt.badge}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>

        </Box>

        {/* ── Fixed footer: input area + Submit — always visible, never scrolls ── */}
        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            pt: 1,
            pb: 1.5,
            borderTop: "1px solid rgba(255,255,255,0.07)",
            background: "linear-gradient(160deg, #0E1525 0%, #070B14 100%)",
          }}
        >
          {/* ── Input area ── */}
          <Box sx={{ bgcolor: "#0D1321", flexShrink: 0 }}>
            <Box
              sx={{
                bgcolor: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.09)",
                borderRadius: "14px",
                p: "10px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                transition: "border-color 0.2s",
                "&:focus-within": { borderColor: "rgba(75,165,235,0.4)" },
              }}
            >
              <TextareaAutosize
                value={customContent.slice(0, 500)}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setCustomContent(e.target.value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={`Tell me what content you want me to create for you.

Example:
Create LinkedIn posts this week about AI digital twin and how Ocesha helps experts grow their business.`}
                minRows={2}
                style={{
                  width: "100%",
                  background: "transparent",
                  color: "#e5e7eb",
                  border: "none",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  resize: "none",
                  outline: "none",
                  lineHeight: 1.6,
                  boxSizing: "border-box",
                }}
              />

              {/* Action row */}
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                {/* Left: mute + mic */}
                <Stack direction="row" spacing={0.8}>
                  {/* Mute / unmute */}
                  <IconButton
                    onClick={toggleMute}
                    title={isMuted ? "Unmute" : "Mute"}
                    sx={{
                      ...iconBtnBase,
                      background: isMuted
                        ? "linear-gradient(135deg, #7C3AED 0%, #60A5FA 100%)"
                        : "linear-gradient(135deg, #4F46E5 0%, #60A5FA 100%)",
                      borderColor: isMuted ? "rgba(255,255,255,0.1)" : "rgba(75,165,235,0.25)",
                      boxShadow: !isMuted
                        ? "0 0 25px rgba(96,165,250,0.6)"
                        : "0 8px 24px rgba(96,165,250,0.25)",
                    }}
                  >
                    {isMuted ? (
                      <VolumeOffIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <VolumeUpIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>

                  {/* Mic */}
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",

                      "&::before": isRecordingQuestion
                        ? {
                          content: '""',
                          position: "absolute",
                          width: "100%",
                          height: "100%",
                          borderRadius: "50%",
                          border: "2px solid rgba(96,165,250,0.65)",
                          animation: "recordRipple 1.8s infinite",
                          backdropFilter: "blur(6px)",
                        }
                        : {},

                      "&::after": isRecordingQuestion
                        ? {
                          content: '""',
                          position: "absolute",
                          width: "100%",
                          height: "100%",
                          borderRadius: "50%",
                          border: "2px solid rgba(167,139,250,0.45)",
                          animation: "recordRipple 1.8s infinite 0.9s",
                          backdropFilter: "blur(6px)",
                        }
                        : {},

                      "@keyframes recordRipple": {
                        "0%": {
                          transform: "scale(1)",
                          opacity: 0.8,
                        },
                        "100%": {
                          transform: "scale(2.3)",
                          opacity: 0,
                        },
                      },
                    }}
                  >
                    <IconButton
                      onClick={() =>
                        isRecordingQuestion
                          ? stopRecording()
                          : startRecording("question")
                      }
                      title={
                        isRecordingQuestion
                          ? "Stop recording"
                          : "Record voice"
                      }
                      sx={{
                        ...iconBtnBase,
                        position: "relative",
                        zIndex: 2,

                        borderRadius: "50%",

                        background: isRecordingQuestion
                          ? "linear-gradient(135deg, #8B5CF6 0%, #60A5FA 100%)"
                          : "linear-gradient(135deg, #4F46E5 0%, #60A5FA 100%)",

                        border: isRecordingQuestion
                          ? "1px solid rgba(167,139,250,0.6)"
                          : "1px solid rgba(255,255,255,0.1)",

                        boxShadow: isRecordingQuestion
                          ? `
                            0 0 0 4px rgba(124,58,237,0.15),
                            0 0 25px rgba(96,165,250,0.55),
                            inset 0 1px 0 rgba(255,255,255,0.2)
                          `
                          : "0 8px 24px rgba(96,165,250,0.25)",

                        transition: "all 0.25s ease",

                        "&:hover": {
                          transform: "scale(1.06)",
                        },
                      }}
                    >
                      <MicNoneOutlinedIcon
                        sx={{
                          fontSize: 20,
                          color: "#fff",
                        }}
                      />
                    </IconButton>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={0.8}>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.4)",
                      fontSize: 11,
                      padding: "10px 0",
                    }}
                  >
                    {customContent.length}/500
                  </Typography>

                  {/* Submit */}
                  <Button
                    onClick={handleSend}
                    disabled={isSending}
                    startIcon={
                      isSending ? (
                        <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />
                      ) : (
                        <CheckCircleIcon sx={{ fontSize: 17 }} />
                      )
                    }
                    sx={{
                      height: 35,
                      px: 2,
                      borderRadius: "10px",
                      textTransform: "none",
                      bgcolor: "#1E5FA0",
                      border: "1px solid",
                      borderColor: "#2573be",
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "#2573be",
                        transform: "scale(1.03)",
                      },
                      "&:disabled": { opacity: 0.5, color: "#fff" },
                    }}
                  >
                    {isSending ? "Sending..." : "Submit"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>

          {/* ── What should I do ── */}
          <Box sx={{ mt: 1.2 }}>
            <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.8 }}>
              <VerifiedIcon sx={{ fontSize: 13, color: "#10B981" }} />
              <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,0.4)" }}>
                I'll use your Digital Twin knowledge to create content that sounds like you.
              </Typography>
            </Stack>
          </Box>

        </Box>
      </Dialog>

      {/* ── Coming Soon modal ── */}
      <Dialog
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: "linear-gradient(160deg, #171029 0%, #0B0E1A 100%)",
            borderRadius: "22px",
            border: "1px solid rgba(124,58,237,0.25)",
            boxShadow: "0 30px 60px rgba(0,0,0,0.6)",
            overflow: "hidden",
          },
        }}
        BackdropProps={{ sx: { backdropFilter: "blur(6px)", background: "rgba(0,0,0,0.6)" } }}
      >
        <Box sx={{ p: 3.5, textAlign: "center", position: "relative" }}>
          <IconButton
            onClick={() => setComingSoonOpen(false)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              width: 30,
              height: 30,
              color: "rgba(255,255,255,0.5)",
              "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>

          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "16px",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, rgba(124,58,237,0.25), rgba(59,130,246,0.15))",
              border: "1px solid rgba(124,58,237,0.35)",
            }}
          >
            <RocketLaunchIcon sx={{ fontSize: 26, color: "#A78BFA" }} />
          </Box>

          <Typography sx={{ color: "#fff", fontSize: 17, fontWeight: 800, mb: 0.8 }}>
            {comingSoonLabel} — Coming Soon
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 13, lineHeight: 1.6, mb: 2.5 }}>
            We're still building this. {comingSoonLabel} generation isn't available yet, but it's on the way — check back soon!
          </Typography>

          <Button
            fullWidth
            onClick={() => setComingSoonOpen(false)}
            sx={{
              textTransform: "none",
              borderRadius: "12px",
              py: 1,
              background: "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              boxShadow: "0 8px 24px rgba(124,58,237,0.35)",
              "&:hover": { background: "linear-gradient(135deg, #8B4FF0 0%, #5B52F0 100%)" },
            }}
          >
            Got it
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default MyDigitalTwinPopup;